import React, { useMemo, useState } from 'react';
import { useCart } from '../context/CartContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar
} from 'recharts';

const Dashboard = () => {
  const { orders } = useCart();
  const [period, setPeriod] = useState('week');

  const now = new Date();
  const startDate = new Date(now);
  const prevStartDate = new Date(now);

  if (period === 'day') {
    startDate.setDate(now.getDate() - 1);
    prevStartDate.setDate(now.getDate() - 2);
  } else if (period === 'week') {
    startDate.setDate(now.getDate() - 7);
    prevStartDate.setDate(now.getDate() - 14);
  } else if (period === 'month') {
    startDate.setMonth(now.getMonth() - 1);
    prevStartDate.setMonth(now.getMonth() - 2);
  } else {
    startDate.setFullYear(2000);
    prevStartDate.setFullYear(2000);
  }

  const [currentOrders, previousOrders] = useMemo(() => {
    const current = orders.filter(o => new Date(o.createdAt) > startDate);
    const previous = orders.filter(o => new Date(o.createdAt) > prevStartDate && new Date(o.createdAt) <= startDate);
    return [current, previous];
  }, [orders, period]);

  const totalRevenue = currentOrders.reduce((acc, o) => acc + o.total, 0);
  const previousRevenue = previousOrders.reduce((acc, o) => acc + o.total, 0);
  const percentDiff = previousRevenue > 0 ? (((totalRevenue - previousRevenue) / previousRevenue) * 100).toFixed(1) : null;
  const totalOrders = currentOrders.length;

  const topDish = (() => {
    const map = {};
    currentOrders.forEach(order => {
      order.items.forEach(item => {
        map[item.name] = (map[item.name] || 0) + item.quantity;
      });
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]);
    return sorted[0] || ['-', 0];
  })();

  const dailyRevenue = useMemo(() => {
    const map = {};
    currentOrders.forEach(order => {
      const date = new Date(order.createdAt).toISOString().split('T')[0];
      map[date] = (map[date] || 0) + order.total;
    });
    return Object.entries(map).map(([date, total]) => ({ date, total }));
  }, [currentOrders]);

  const dishPie = useMemo(() => {
    const map = {};
    currentOrders.forEach(order => {
      order.items.forEach(item => {
        map[item.name] = (map[item.name] || 0) + item.quantity;
      });
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [currentOrders]);

  const COLORS = ['#34d399', '#60a5fa', '#f472b6', '#facc15', '#f87171', '#a78bfa', '#38bdf8'];

  return (
    <div className="text-white p-6 max-w-6xl mx-auto space-y-12">
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-6 shadow-xl">
        <h1 className="text-4xl font-extrabold text-white mb-2">📊 Панель аналитики</h1>
        <p className="text-gray-400 text-sm mb-4">Сводка по заказам и выручке</p>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Период:</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-gray-900 border border-gray-700 text-white text-sm rounded-md px-3 py-1 shadow-sm"
          >
            <option value="day">Сегодня</option>
            <option value="week">Неделя</option>
            <option value="month">Месяц</option>
            <option value="all">Всё время</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-gray-900 rounded-xl p-6 shadow-md text-center hover:scale-[1.02] transition">
          <p className="text-gray-400 text-sm">📦 Заказов</p>
          <p className="text-4xl font-bold mt-2">{totalOrders}</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-6 shadow-md text-center hover:scale-[1.02] transition">
          <p className="text-gray-400 text-sm">💰 Выручка</p>
          <p className="text-4xl font-bold mt-2">{totalRevenue.toLocaleString()} ₽</p>
          {percentDiff !== null && (
            <p className={`text-sm mt-2 ${percentDiff >= 0 ? 'text-green-400' : 'text-red-400'}`}> 
              {percentDiff >= 0 ? '▲' : '▼'} {Math.abs(percentDiff)}% vs прошлый период
            </p>
          )}
        </div>
        <div className="bg-gray-900 rounded-xl p-6 shadow-md text-center hover:scale-[1.02] transition">
          <p className="text-gray-400 text-sm">🍽 Топ блюдо</p>
          <p className="text-2xl font-bold mt-2">{topDish[0]}</p>
          <p className="text-sm text-teal-400">× {topDish[1]}</p>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
        <h2 className="text-xl font-semibold mb-4">📈 График выручки</h2>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={dailyRevenue}>
            <XAxis dataKey="date" stroke="#ccc" />
            <YAxis stroke="#ccc" />
            <Tooltip />
            <Line type="monotone" dataKey="total" stroke="#60a5fa" strokeWidth={3} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
        <h2 className="text-xl font-semibold mb-4">🥧 Популярность блюд</h2>
        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <Pie data={dishPie} dataKey="value" nameKey="name" outerRadius={110} label>
              {dishPie.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend layout="horizontal" verticalAlign="bottom" align="center" />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-gray-900 rounded-xl p-6 shadow-md">
        <h2 className="text-xl font-semibold">🔮 Прогноз</h2>
        <p className="text-gray-300 mt-2">
          Прогноз (+5%): <span className="text-white font-bold">{(totalRevenue * 1.05).toFixed(0)} ₽</span>
        </p>
        <p className="text-xs text-gray-500 mt-1">* Модель без учёта сезонных факторов</p>
      </div>
    </div>
  );
};

export default Dashboard;